import type { Column, ProjectTicketType, ProjectViewPrefs } from '@vd/types';

/**
 * What is narrowing the board's list, named the way the toolbar and the
 * Filter dialog name it, so an empty Cards or Table view can say which
 * filters hid everything and offer to clear them (TOADO-976).
 *
 * "Show archived" is left out: it adds tickets, so clearing it would not
 * bring any back.
 */
const QUICK_FILTER_NAMES: Record<string, string> = {
  hasAiActivity: 'Has AI activity',
  capturePartial: 'Capture partial',
  unresolved: 'Unresolved',
  mentionsMe: 'Mentions me',
  externalReports: 'From customers',
};

export interface BoardFilterInputs {
  prefs: ProjectViewPrefs;
  columns: readonly Column[];
  ticketTypes?: readonly ProjectTicketType[];
  /** The search box's text. */
  query: string;
  /** People picked in the header's avatar strip. */
  peopleCount: number;
  /**
   * Whether the type filter narrows what is shown. It is applied by the board's
   * own list request, so a search, whose results replace that list, ignores it.
   */
  typeFilterApplies?: boolean;
}

export function activeFilterNames({ prefs, columns, ticketTypes = [], query, peopleCount, typeFilterApplies = true }: BoardFilterInputs): string[] {
  const names: string[] = [];
  for (const [key, on] of Object.entries(prefs.quickFilters)) {
    if (on && QUICK_FILTER_NAMES[key]) names.push(QUICK_FILTER_NAMES[key]!);
  }
  const listOf = (ids: readonly string[], all: readonly { id: string; name: string }[]) =>
    ids.map((id) => all.find((item) => item.id === id)?.name).filter((name): name is string => !!name).join(', ');
  // A pick that takes in every choice hides nothing, so it is not named as
  // what hid the tickets. Until the choices are known it is named, the safe way.
  const narrows = (ids: readonly string[], all: readonly { id: string }[]) => all.length === 0 || all.some((item) => !ids.includes(item.id));
  if (prefs.columnIds?.length && narrows(prefs.columnIds, columns)) names.push(`Column: ${listOf(prefs.columnIds, columns) || prefs.columnIds.length}`);
  // 'none' is the Filter dialog's "No type".
  const typeChoices = [...ticketTypes, { id: 'none', name: 'No type' }];
  if (typeFilterApplies && prefs.typeIds?.length && (ticketTypes.length === 0 || narrows(prefs.typeIds, typeChoices))) {
    names.push(`Type: ${listOf(prefs.typeIds, typeChoices) || prefs.typeIds.length}`);
  }
  if (prefs.assigneeIds?.length) names.push(`Assignee (${prefs.assigneeIds.length})`);
  if (prefs.authorIds?.length) names.push(`Author (${prefs.authorIds.length})`);
  if (prefs.createdFrom || prefs.createdTo) names.push('Created date');
  if (peopleCount > 0) names.push(`People (${peopleCount})`);
  if (query.trim()) names.push(`Search "${query.trim()}"`);
  return names;
}

/**
 * The preferences with every narrowing filter off. Unlike the Filter dialog's
 * "Clear all" it keeps "Show archived" on if it was: that adds tickets, and
 * this is offered when there are none to show. The sort is kept too.
 */
export function clearedFilterPrefs(prefs: ProjectViewPrefs): Partial<ProjectViewPrefs> {
  const quickFilters = { ...prefs.quickFilters };
  for (const key of Object.keys(QUICK_FILTER_NAMES)) {
    if (key in quickFilters) (quickFilters as Record<string, boolean>)[key] = false;
  }
  return {
    quickFilters,
    columnIds: [],
    assigneeIds: [],
    authorIds: [],
    createdFrom: undefined,
    createdTo: undefined,
    typeIds: undefined,
  };
}
